import { createSlice, createAsyncThunk, current } from "@reduxjs/toolkit";
import { api } from "../../api/httpclient";
import { addNotification } from "./notificationsslice";
import {
  sortArrayByString,
  getComparisonOperators,
  getAggregateFunctions,
  getAlertImportances,
  getAlertRecurrsEveryIntervals,
  getAlertCoveredPeriodIntervals,
  getAlertVariantCoveredPeriodIntervals,
  collectParametersFromRules,
} from "../../lib/utils";
import { formatQuery } from "react-querybuilder";
import {
  notificationDurationShort,
  notificationDurationMiddle,
  notificationDurationLong,
} from "../../lib/utils";
import { AggregateFunction } from "../../lib/aggregatefunctions";
import i18next from "i18next";

const defaultDataset = {
  Id: 0,
  Title: "None",
  Description: "",
  CategoryId: 0,
  Alerts: [],
  Fields: [],
  Users: [],
  Where: [],
  OrderId: -1,
};
const defaultDatasetId = 0;

const defaultFormInitialValues = {
  Id: 0,
  DatasetId: defaultDatasetId,
  Title: "",
  Importance: 0,
  Description: "",
  IsVariant: false,
  VariantCoveredPeriod: 0,
  IsAggregate: true, //relies on an aggregation expression not agg. function
  AggregateExpressionId: null,
  AggregateFunctionId: 0,
  FieldId: null,
  OperatorId: 0,
  TargetValueMin: 0,
  TargetValueMax: 0,
  TimeFieldId: 0,
  RecurrsEvery: 0,
  CoveredPeriod: 0,
  NotifyThroughApplication: true,
  NotifyThroughEmail: true,
  Url: "",
  Interpretation: "",
  GroupedFields: [],
  IsActive: true,
  WhereObject: "",
  WhereStatement: "",
  WhereParameters: "",
  Viewers: [],
  Groups: [],

  generalOpen: true,
  miscOpen: true,
  expressionOpen: true,
  groupByOpen: false,
  filterOpen: false,
};

const initialState = {
  pageLoaded: false,
  pageDisposed: true,
  processing: false,
  processingText: "",
  mode: 0,
  currentDataset: defaultDataset,
  formKey: new Date().toString(),
  formInitialValues: { ...defaultFormInitialValues },
  formValues: { ...defaultFormInitialValues },
  formServerErrors: [],
  lookups: {
    datasets: [],
  },
  toolsVisible: true,
  panes: [
    {
      // size: "30%",
      size: "22%",
      min: "20%",
      max: "25%",
      collapsible: false,
    },
    {
      //min: "700px",
      //size:"60%",
      min: "75%",
      max: "80%",
      collapsible: false,
    },
  ],
};

const alertsPageSlice = createSlice({
  name: "alertsPage",
  initialState: initialState,
  reducers: {
    setInitialState: (state, action) => ({ ...initialState }), //no payload

    setProcessing: (state, action) => {
      //payload: {processing=true, processingText=false}, when processing is false, processingText is ""
      const { processing = true, processingText = "" } = action.payload;

      return {
        ...state,
        processing: processing,
        processingText: processing == false ? "" : processingText,
      };
    },

    setPanes: (state, action) => ({ ...state, panes: { ...action.payload } }),

    setPageValue: (state, action) => ({
      ...state,
      [action.payload.id]: action.payload.value,
    }), //payload: {id: , value:}

    setFormValue: (state, action) => {
      //payload: {id: fieldId, value: fieldValue}
      let id = action.payload.id;
      let value = action.payload.value;

      return {
        ...state,
        formValues: { ...state.formValues, [id]: value },
      };
    },

    setFieldValue: (state, action) => {
      //payload is the field - sending a field => replace the field with that id with the passed field's properties
      const pageState = current(state);
      const entity = action.payload;

      return {
        ...state,
        formValues: {
          ...state.formValues,
          Fields: state.formValues.Fields.map((item) => {
            if (item.FieldId == entity.FieldId) return { ...entity };
            else return item;
          }),
        },
      };
    },

    setFormServerErrors: (state, action) => ({
      ...state,
      formServerErrors: [...action.payload],
    }),

    pageLoaded: (state, action) => {
      //payload: {Datasets: includes the default one  }
      const pageState = current(state);
      if (pageState.pageDisposed) return state;

      return {
        ...state,
        pageLoaded: true,
        processing: false,
        processingText: "",
        mode: 0,
        currentDataset: defaultDataset,
        formKey: new Date().toString(),
        formValues: { ...defaultFormInitialValues },
        formInitialValues: { ...defaultFormInitialValues },
        formServerErrors: [],
        lookups: { datasets: action.payload.Datasets },
      };
    },

    datasetLoaded: (state, action) => {
      //payload: { datasetId: , alerts: [], fields: [], users: [], groups: [], where: []}
      const pageState = current(state);

      if (pageState.pageDisposed) return state;

      let currentDataset = pageState.lookups.datasets.find(
        (e) => e.Id == action.payload.datasetId,
      );

      //aggregate expressions - already sorted by title by api
      let aggregateExpressions = action.payload.fields
        .filter(
          (e) =>
            e.IsExpression == true &&
            e.IsAggregate == true &&
            e.DataTypeId == 2,
        )
        .map((item) => ({ Id: item.Id, Title: item.Title, Name: item.Name }));
      //aggregateExpressions = sortArrayByString(aggregateExpressions, "Title");
      aggregateExpressions = [
        { Id: null, Title: "None", Name: "None" },
        ...aggregateExpressions,
      ];

      //fields - already sorted by title by api
      //let fields = action.payload.fields.filter(e => (e.IsExpression == false && e.IsAggregate==false && e.DataTypeId == 2)).map(item => ({Id: item.Id, Title: item.Title, Name: item.Name}));
      let fields = action.payload.fields
        .filter((e) => e.IsExpression == false && e.IsAggregate == false)
        .map((item) => ({
          Id: item.Id,
          Title: item.Title,
          Name: item.Name,
          DataTypeId: item.DataTypeId,
        }));
      //fields = sortArrayByString(fields, "Title");
      fields = [
        { Id: null, Title: "None", Name: "None", DataTypeId: 0 },
        ...fields,
      ];

      //time fields - already sorted by title by api
      let timeFields = action.payload.fields
        .filter(
          (e) =>
            e.IsExpression == false &&
            e.IsAggregate == false &&
            e.DataTypeId == 5 &&
            e.IsUTC == true,
        )
        .map((item) => ({ Id: item.Id, Title: item.Title, Name: item.Name }));
      //timeFields = sortArrayByString(timeFields, "Title");
      timeFields = [{ Id: 0, Title: "None", Name: "None" }, ...timeFields];

      //users - already sorted by name by api
      let users = action.payload.users.map((item) => ({
        Id: item.Id,
        Name: item.Name,
        IsActive: item.IsActive,
        Checked: false,
      }));
      //users = sortArrayByString(users, "Name");

      //groups - already sorted by name by api
      let groups = action.payload.groups.map((item) => ({
        Id: item.Id,
        Name: item.Name,
        Checked: false,
      }));

      //groupby fields - fields excluding is expression and excluding is aggregate and those that are not groupable in alert
      let groupByFields = action.payload.fields
        .filter((e) => e.IsAggregate == false && e.IsGroupableInAlert == true)
        .map((item) => ({
          Id: item.Id,
          Title: item.Title,
          Name: item.Name,
          Checked: false,
        }));
      //let groupByFields = action.payload.fields.filter(e => (e.IsExpression == false && e.IsAggregate==false && e.IsGroupableInAlert == true)).map(item => ({Id: item.Id, Title: item.Title, Name: item.Name, Checked: false}));

      //where
      let where = action.payload.where;
      for (let prop in where) {
        //must add name and label (lowercases) to every field
        where[prop].name = where[prop].Name;
        where[prop].label = where[prop].Title;

        if (where[prop].DataTypeId == 1) {
          where[prop].inputType = "bool";
          //where[prop].dataType="bool";
        } else if (where[prop].DataTypeId == 2) {
          where[prop].inputType = "number";
          //where[prop].dataType="number";
        } else if (where[prop].DataTypeId == 3) {
          where[prop].inputType = "string";
          //where[prop].dataType="string";
        } else if (where[prop].DataTypeId == 4 || where[prop].DataTypeId == 5) {
          where[prop].inputType = "date";
        }

        if (where[prop].IsLookup) {
          for (let p in where[prop].values) {
            where[prop].values[p].label = where[prop].values[p].Title;
          }
        }
      }

      return {
        ...state,
        processing: false,
        processingText: "",
        mode: 0,
        currentDataset: {
          ...currentDataset,
          Alerts: [...action.payload.alerts],
          Fields: [...action.payload.fields],
          Users: [...action.payload.users],
          Groups: [...action.payload.groups],
          Where: where,
          Lookups: {
            AggregateExpressions: aggregateExpressions,
            Fields: fields,
            AggregateFunctions: [
              { Id: 0, Title: "None", Name: "None" },
              ...AggregateFunction.functions
                .sort((a, b) =>
                  a.title < b.title ? -1 : a.title == b.title ? 0 : 1,
                )
                .map((e) => ({
                  Id: e.id,
                  Title: e.title,
                  Name: e.name,
                  Description: e.description,
                  AppliesTo: e.appliesTo,
                })),
            ], //getAggregateFunctions(),
            Operators: getComparisonOperators(),
            AlertImportances: getAlertImportances().map((col) => ({
              ...col,
              Title: i18next.t(col.Title),
            })),
            AlertRecurrsEveryIntervals: getAlertRecurrsEveryIntervals(),
            AlertCoveredPeriodIntervals: getAlertCoveredPeriodIntervals(),
            AlertVariantCoveredPeriodIntervals:
              getAlertVariantCoveredPeriodIntervals(),
            TimeFields: timeFields,
            Users: users,
            Groups: groups,
            GroupByFields: groupByFields,
          },
        },
        formKey: new Date().toString(),
        formInitialValues: { ...defaultFormInitialValues },
        formValues: { ...defaultFormInitialValues },
        formServerErrors: [],
      };
    },

    entityLoaded: (state, action) => {
      //action.payload: the fetched entity
      const pageState = current(state);
      const currentDataset = pageState.currentDataset;

      if (pageState.pageDisposed) return state;

      let viewers = currentDataset.Lookups.Users.map((item) => {
        if (action.payload.Viewers.find((e) => e.UserId == item.Id))
          return { ...item, Checked: true };
        else return { ...item, Checked: false };
      });

      let groups = currentDataset.Lookups.Groups.map((item) => {
        if (action.payload.Groups.find((e) => e.GroupId == item.Id))
          return { ...item, Checked: true };
        else return { ...item, Checked: false };
      });

      let groupedFields = currentDataset.Lookups.GroupByFields.map((item) => {
        if (action.payload.GroupedFields.find((e) => e.FieldId == item.Id))
          return { ...item, Checked: true };
        else return { ...item, Checked: false };
      });

      return {
        ...state,
        processing: false,
        processingText: "",
        mode: 2,
        formKey: new Date().toString(),
        formInitialValues: {
          ...action.payload,
          Viewers: viewers,
          Groups: groups,
          GroupedFields: groupedFields,
          WhereObject: JSON.parse(action.payload.WhereObject),
          generalOpen: true,
          miscOpen: true,
          expressionOpen: true,
          groupByOpen: true,
          filterOpen: true,
        },
        formValues: {
          ...action.payload,
          Viewers: viewers,
          Groups: groups,
          GroupedFields: groupedFields,
          WhereObject: JSON.parse(action.payload.WhereObject),
          generalOpen: true,
          miscOpen: true,
          expressionOpen: true,
          groupByOpen: true,
          filterOpen: true,
        },
        formServerErrors: [],
      };
    },

    createEntity: (state, action) => {
      const pageState = current(state);
      const currentDataset = pageState.currentDataset;
      const currentDatasetId = currentDataset.Id;

      return {
        ...state,
        mode: 1,
        formKey: new Date().toString(),
        formInitialValues: {
          ...defaultFormInitialValues,
          DatasetId: currentDatasetId,
          Viewers: [...currentDataset.Lookups.Users],
          Groups: [...currentDataset.Lookups.Groups],
          GroupedFields: [...currentDataset.Lookups.GroupByFields],
        },
        formValues: {
          ...defaultFormInitialValues,
          DatasetId: currentDatasetId,
          Viewers: [...currentDataset.Lookups.Users],
          Groups: [...currentDataset.Lookups.Groups],
          GroupedFields: [...currentDataset.Lookups.GroupByFields],
        },
        formServerErrors: [],
      };
    },

    removeEntityFromUI: (state, action) => {
      //payload: the entity id
      let pageState = current(state);
      let entityId = action.payload;
      let newCurrentDataset = { ...pageState.currentDataset };

      //compute currentDataset, IF it contains the removed entity, by removing the alert from it
      if (newCurrentDataset.Alerts.find((e) => e.Id == entityId))
        newCurrentDataset = {
          ...newCurrentDataset,
          Alerts: newCurrentDataset.Alerts.filter((e) => e.Id != entityId),
        };

      //if it's the current entity => goto no mode
      let currentMode = {};
      if (pageState.mode == 2 && pageState.formValues.Id == entityId)
        currentMode = {
          mode: 0,
          formKey: new Date().toString(),
          formInitialValues: {
            ...defaultFormInitialValues,
            // DatasetId: pageState.currentDataset.Id,
            // Viewers: [...pageState.currentDataset.Lookups.Users],
            // GroupedFields: [...pageState.currentDataset.Lookups.GroupByFields]
          },
          formValues: {
            ...defaultFormInitialValues,
            // DatasetId: pageState.currentDataset.Id,
            // Viewers: [...pageState.currentDataset.Lookups.Users],
            // GroupedFields: [...pageState.currentDataset.Lookups.GroupByFields]
          },
          formServerErrors: [],
        };

      return { ...state, currentDataset: newCurrentDataset, ...currentMode };
    },

    entityInserted: (state, action) => {
      //payload: {id: }
      const pageState = current(state);
      if (pageState.pageDisposed) return state;

      const id = action.payload.id;
      const insertedEntity = { ...pageState.formValues, Id: id };

      let currentDataset = { ...pageState.currentDataset };

      //add entity to it
      currentDataset = {
        ...currentDataset,
        Alerts: [
          ...currentDataset.Alerts,
          {
            ...insertedEntity,
            IsGrouped:
              pageState.formValues.GroupedFields.filter(
                (e) => e.Checked == true,
              ).length > 0,
          },
        ],
      };

      return {
        ...state,
        mode: 2,
        currentDataset: currentDataset,
        formKey: new Date().toString(),
        formValues: { ...insertedEntity },
        formInitialValues: { ...insertedEntity },
        formServerErrors: [],
      };
    },

    entityUpdated: (state, action) => {
      const pageState = current(state);
      if (pageState.pageDisposed) return state;

      const entityId = pageState.formValues.Id;

      //rebind in list
      let currentDataset = { ...pageState.currentDataset };

      //remove -> add entity to it
      currentDataset = {
        ...currentDataset,
        Alerts: currentDataset.Alerts.map((item) => {
          if (item.Id == entityId)
            return {
              ...pageState.formValues,
              IsGrouped:
                pageState.formValues.GroupedFields.filter(
                  (e) => e.Checked == true,
                ).length > 0,
            };
          else return item;
        }),
      };

      return {
        ...state,
        currentDataset: currentDataset,
        formKey: new Date().toString(),
        formInitialValues: { ...state.formValues },
        formServerErrors: [],
      };
    },

    entityCloned: (state, action) => {
      //payload: {id: , title: , navigate:  }
      const pageState = current(state);

      if (pageState.pageDisposed) return state;

      const id = action.payload.id;
      const title = action.payload.title;
      const navigate = action.payload.navigate;

      const insertedEntity = {
        ...pageState.formValues,
        Id: id,
        Title: title,
        OrderId: 0,
        IsSystem: false,
      };

      let currentDataset = { ...pageState.currentDataset };

      //add entity to it
      currentDataset = {
        ...currentDataset,
        Alerts: [
          ...currentDataset.Alerts,
          {
            ...insertedEntity,
            IsGrouped:
              pageState.formValues.GroupedFields.filter(
                (e) => e.Checked == true,
              ).length > 0,
          },
        ],
      };

      return {
        ...state,
        currentDataset: currentDataset,
      };
    },

    entityDeleted: (state, action) => {
      //payload: id of the deleted entity
      const pageState = current(state);
      if (pageState.pageDisposed) return state;

      const deletedEntityId = action.payload;
      let isCurrentEntity = false;

      if (pageState.formValues.Id == deletedEntityId) isCurrentEntity = true;

      //remove the entity from currentDataset
      let newCurrentDataset = {
        ...pageState.currentDataset,
        Alerts: [
          ...pageState.currentDataset.Alerts.filter(
            (e) => e.Id != deletedEntityId,
          ),
        ],
      };

      let newMode = {};
      if (isCurrentEntity)
        newMode = {
          mode: 0,
          formKey: new Date().toString(),
          formInitialValues: { ...defaultFormInitialValues },
          formValues: { ...defaultFormInitialValues },
          formServerErrors: [],
        };

      return { ...state, currentDataset: newCurrentDataset, ...newMode };
    },

    disposePage: (state, action) => {
      //payload: bool
      if (action.payload) return { ...initialState };
      else return { ...state, pageDisposed: false };
    },
  },
});

export default alertsPageSlice.reducer;

export const loadPage = (t) => async (dispatch, getState) => {
  //start with initial values
  dispatch(setInitialState());

  //undispose page
  dispatch(disposePage(false));

  //show loader
  dispatch(
    setProcessing({
      processing: true,
      processingText: t("slices.loadingPage"),
    }),
  );

  try {
    var response = await api({
      url: "alert/getalertspagedata",
      method: "post",
      data: {},
      //headers:{'h':'1'}
    });

    //i am here +. i can safely access response.data.Succeeded which is always true and response.data.Data
    if (getState().alertsPage.pageDisposed) return;

    //it's ok to dispatch here
    let data = response.data.Data;

    //transform the response
    //add the 0/None dataset

    data.Datasets.splice(0, 0, { ...defaultDataset });

    dispatch(pageLoaded(data));

    return Promise.resolve(response);
  } catch (err) {
    //i am here => could be any error included 200 and Succeeded=false
    return Promise.reject(err);
  }
};

export const loadDataset = (datasetId, t) => async (dispatch, getState) => {
  //show loading
  dispatch(
    setProcessing({
      processing: true,
      processingText: t("slices.loadingDataset"),
    }),
  );

  if (datasetId == 0) {
    dispatch(
      datasetLoaded({
        datasetId: 0,
        alerts: [],
        fields: [],
        users: [],
        where: [],
      }),
    );
    return;
  }

  try {
    var response = await api({
      url: "alert/getalertsbydatasetid",
      method: "post",
      data: {
        datasetId: datasetId,
      },
      //headers:{'h':'1'}
    });

    //i am here +. i can safely access response.data.Succeeded which is always true and response.data.Data
    if (getState().alertsPage.pageDisposed) return;

    let data = response.data.Data;

    dispatch(
      datasetLoaded({
        datasetId: datasetId,
        alerts: data.Alerts,
        fields: data.Fields,
        users: data.Users,
        groups: data.SecurityGroups,
        where: data.Where,
      }),
    );

    return Promise.resolve(response);
  } catch (err) {
    //i am here => could be any error included 200 and Succeeded=false
    return Promise.reject(err);
  }
};

export const loadEntity = (entityId, t) => async (dispatch, getState) => {
  //show loader
  dispatch(
    setProcessing({
      processing: true,
      processingText: t("slices.loadingAlert"),
    }),
  );

  try {
    var response = await api({
      url: "alert/getalertbyid",
      method: "post",
      data: {
        id: entityId,
      },
      //headers:{'h':'1'}
    });

    //i am here +. i can safely access response.data.Succeeded which is always true and response.data.Data
    if (getState().alertsPage.pageDisposed) return;

    let data = response.data.Data;

    //transform data
    dispatch(entityLoaded(data));

    return Promise.resolve(response);
  } catch (err) {
    //i am here => could be any error included 200 and Succeeded=false
    return Promise.reject(err);
  }
};

export const saveEntity = (t) => async (dispatch, getState) => {
  const pageState = getState().alertsPage;

  let entityId = pageState.formValues.Id;
  let url = pageState.mode == 1 ? "alert/createalert" : "alert/updatealert";
  let title =
    pageState.mode == 1 ? t("slices.creatingAlert") : t("slices.updatingAlert");

  //show loader
  dispatch(setProcessing({ processing: true, processingText: title }));

  //some tramsformation is needed
  let whereObject = JSON.stringify(pageState.formValues.WhereObject);
  let whereStatement = "";
  let whereParameters = [];

  // generate parameterized SQL
  const formatted = formatQuery(
    pageState.formValues.WhereObject,
    "parameterized",
  );

  whereParameters =
    pageState.formValues.WhereObject?.rules?.length > 0
      ? collectParametersFromRules(pageState.formValues.WhereObject.rules)
      : [];

  if (
    (whereParameters != null && whereParameters.length > 0) ||
    (pageState.formValues.WhereObject.rules &&
      pageState.formValues.WhereObject.rules.length > 0)
  ) {
    whereStatement = formatted.sql;
  }
  
  // //some tramsformation is needed
  // let whereObject = JSON.stringify(pageState.formValues.WhereObject);
  // let whereStatement = "";
  // let whereParameters = [];

  // let parameters = formatQuery(
  //   pageState.formValues.WhereObject,
  //   "parameterized"
  // ).params;

  // if (
  //   (parameters != null && parameters.length > 0) ||
  //   (pageState.formValues.WhereObject.rules &&
  //     pageState.formValues.WhereObject.rules.length > 0)
  // ) {
  //   whereStatement = formatQuery(
  //     pageState.formValues.WhereObject,
  //     "parameterized"
  //   ).sql;
  //   whereParameters = parameters;
  // }

  let entity = {
    ...pageState.formValues,
    TargetValueMin: parseFloat(pageState.formValues.TargetValueMin),
    TargetValueMax:
      pageState.formValues.OperatorId == 7 ||
      pageState.formValues.OperatorId == 8
        ? parseFloat(pageState.formValues.TargetValueMax)
        : parseFloat(pageState.formValues.TargetValueMin),

    Viewers: pageState.formValues.Viewers.filter(
      (item) => item.Checked == true,
    ).map((item) => ({ UserId: item.Id })),
    Groups: pageState.formValues.Groups.filter(
      (item) => item.Checked == true,
    ).map((item) => ({ GroupId: item.Id })),
    GroupedFields: pageState.formValues.GroupedFields.filter(
      (item) => item.Checked == true,
    ).map((item) => ({ FieldId: item.Id })),
    WhereObject: whereObject,
    WhereStatement: whereStatement,
    Parameters: whereParameters,
  };

  try {
    var response = await api({
      url: url,
      method: "post",
      data: { ...entity },
      //headers:{'h':'1'}
    });

    //i am here +. i can safely access response.data.Succeeded which is always true and response.data.Data
    if (getState().alertsPage.pageDisposed) return;

    let data = response.data.Data;

    if (pageState.mode == 1) dispatch(entityInserted({ id: data.Id }));
    else dispatch(entityUpdated());

    let successMessage =
      pageState.mode == 1 ? t("slices.alertCreated") : t("slices.alertUpdated");

    dispatch(
      addNotification({
        text: successMessage,
        type: "success",
        closable: true,
        duration: notificationDurationMiddle,
        position: "topRight",
      }),
    );

    dispatch(setProcessing({ processing: false }));

    return Promise.resolve(response);
  } catch (err) {
    //i am here => could be any error included 200 and Succeeded=false
    return Promise.reject(err);
  }
};

export const deleteEntity = (id, t) => async (dispatch, getState) => {
  //payload the entity id
  dispatch(
    setProcessing({
      processing: true,
      processingText: t("slices.deletingAlert"),
    }),
  );

  try {
    var response = await api({
      url: "/alert/removealert",
      method: "post",
      data: { id: id },
      //headers:{'h':'1'}
    });

    //i am here +. i can safely access response.data.Succeeded which is always true and response.data.Data
    if (getState().alertsPage.pageDisposed) return;

    let data = response.data.Data;

    dispatch(
      addNotification({
        text: t("slices.deletedAlert"),
        type: "success",
        closable: true,
        duration: notificationDurationMiddle,
        position: "topRight",
      }),
    );

    dispatch(entityDeleted(id));

    dispatch(setProcessing({ processing: false }));

    return Promise.resolve(response);
  } catch (err) {
    //i am here => could be any error included 200 and Succeeded=false
    return Promise.reject(err);
  }
};

export const {
  setInitialState,
  setProcessing,
  setPageValue,
  pageLoaded,
  setPanes,
  datasetLoaded,
  createEntity,
  setFormValue,
  setFormServerErrors,
  entityLoaded,
  removeEntityFromUI,
  entityInserted,
  entityUpdated,
  entityCloned,
  entityDeleted,
  setFieldValue,
  disposePage,
} = alertsPageSlice.actions;
